import { type FC, type RefObject, type PropsWithChildren } from 'react'
import { Box, Typography, useTheme } from '@dhl-ui/components'
import type { Festive } from './festives'

import { DeliveryVan, type DeliveryVanHandle } from './DeliveryVan'
import { pulse } from './pulse'
import { illustrationHeight, svgHeight, triggerHeight } from './constants'

type Props = PropsWithChildren<{
  inputRef: RefObject<HTMLDivElement | null>
  vanRef: RefObject<DeliveryVanHandle | null>
  translateY: number
  isRetracting: boolean
  isRefreshing: boolean
  lastUpdateText: string
  festive?: Festive
}>

export const PullContainer: FC<Props> = ({
  children,
  inputRef,
  vanRef,
  translateY,
  isRetracting,
  isRefreshing,
  lastUpdateText,
  festive,
}) => {
  const { breakpoints } = useTheme()
  const isHidden = translateY < 0

  return (
    <Box
      ref={inputRef}
      data-testid="pull-container"
      position="relative"
      style={{
        transform: translateY >= 0 ? `translateY(${translateY}px)` : undefined,
      }}
      sx={{
        transition: 'transform 0.05s ease-out',
        transitionDuration: isRetracting ? '.2s' : '.05s',
        top: isHidden ? 0 : -triggerHeight,
        [breakpoints.up('lg')]: {
          top: 0,
        },
      }}
      zIndex={1}
    >
      <Box
        alignItems="center"
        data-test="refresh"
        flexDirection="column"
        height={illustrationHeight}
        justifyContent="center"
        sx={{
          display: {
            xs: isHidden ? 'none' : 'flex',
            xl: 'none',
          },
        }}
      >
        <DeliveryVan ref={vanRef} height={svgHeight} festive={festive} />

        <Typography
          color="tertiary"
          variant="caption1"
          sx={{
            animation: isRefreshing ? `${pulse} .8s linear 0s infinite alternate` : undefined,
          }}
        >
          {lastUpdateText}
        </Typography>
      </Box>
      <Box sx={{ pt: isHidden ? 0 : 2 }}>{children}</Box>
    </Box>
  )
}
